// Plan-feature flags table for /usage.
//
// Read-only ✓ / ✗ list for the four user-relevant capabilities. The BE
// /subscriptions/features payload includes EVERY registered feature
// key; we only render the subset that's customer-meaningful. Other
// keys (BULK_EXPORT, COMPREHENSIVE_ANALYSIS, CASE_DETAILS_FETCH)
// stay hidden because they don't drive a user-visible workflow on
// their own.

import { Card, Skeleton, Table, Tag, Typography } from "antd";
import { CheckCircleTwoTone, CloseCircleTwoTone } from "@ant-design/icons";
import { usePlanFeatures } from "../../hooks/usePlanFeatures";
import { FEATURE_KEYS } from "../../utils/featureKeys";

const { Text } = Typography;

// Display order + customer-friendly descriptions. Keep this list tight
// — every entry here surfaces to the customer.
const USAGE_FEATURE_ROWS = [
  {
    key: FEATURE_KEYS.AI_RESPONSE_GENERATION,
    label: "AI Response Generation",
    description: "Draft AI-assisted replies to notices.",
  },
  {
    key: FEATURE_KEYS.LEGAL_RESPONSE_GENERATION,
    label: "Legal Response",
    description: "Premium legal-style responses with case fragments.",
  },
  {
    key: FEATURE_KEYS.VIEW_NOTICE_SUMMARY,
    label: "View Notice Summary",
    description: "Quick AI-generated summary panel for every notice.",
  },
  {
    key: FEATURE_KEYS.MULTI_USER_SUPPORT,
    label: "Multi-User Support",
    description: "Add team members and assign clients across staff.",
  },
];

export default function FeatureFlagsTable() {
  const { features, metadata, isLoading } = usePlanFeatures();

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  const rows = USAGE_FEATURE_ROWS.map((row) => {
    const enabled = !!features?.[row.key];
    const meta = metadata?.[row.key] || {};
    return {
      key: row.key,
      label: meta.label || row.label,
      description: meta.description || row.description,
      enabled,
    };
  });

  const columns = [
    {
      title: "Capability",
      dataIndex: "label",
      width: 220,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
      render: (text) => <Text type="secondary">{text}</Text>,
    },
    {
      title: "Status",
      dataIndex: "enabled",
      width: 110,
      align: "right",
      render: (enabled) =>
        enabled ? (
          <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">
            Included
          </Tag>
        ) : (
          <Tag icon={<CloseCircleTwoTone twoToneColor="#9ca3af" />}>Not included</Tag>
        ),
    },
  ];

  return (
    <Card>
      <Table
        size="middle"
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
      />
    </Card>
  );
}
